import React, { useCallback } from 'react';

import { BONUS_PER_DAY } from '@api/routers/user/boost.constant';
import { IPersonaFront } from '@api/routers/user/service';
import styled from '@emotion/styled';
import { useMutation } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useAccount, useWaitForTransactionReceipt } from 'wagmi';

import { READ_RECEIPT_TIMEOUT } from '@web/features/staking/utils';
import { Button, showSnackbar } from '@web/ui/components';
import { IModalProps } from '@web/ui/components/modal';
import { clients } from '@web/utils/rainbowkit/chains';
import { trpcReact } from '@web/utils/trpc/trpcReact';

import { boost } from '../service';

import { BaseStakingModal } from './BaseStakingModal';
import { StakingAlert } from './components/StakingAlert';
import { StakingStep } from './components/StakingStep';
import { getModalStatus } from './getModalStatus';

const Action = styled(Button)`
  white-space: nowrap;
`;

export interface IBoostModalProps extends IModalProps {
  personas: IPersonaFront[];
}

export const BoostModal = ({
  personas,
  onClose,
  ...props
}: IBoostModalProps) => {
  const t = useTranslations();

  const account = useAccount();
  const generateSignature = trpcReact.user.generateBoostSignature.useMutation();
  const afterBoostTransaction =
    trpcReact.user.afterBoostTransaction.useMutation();
  const boostMutation = useMutation({
    mutationFn: boost,
    meta: {
      notifyError: true,
    },
  });
  const boostTransactionReceipt = useWaitForTransactionReceipt({
    hash: boostMutation.data,
    timeout: READ_RECEIPT_TIMEOUT,
    query: {
      meta: {
        notifyError: true,
      },
    },
  });

  const status = getModalStatus([
    generateSignature.status,
    boostMutation.status,
    boostTransactionReceipt.status,
  ]);

  const handleBoost = useCallback(async () => {
    const tokenIds = personas.map((p) => p.tokenId);

    const { bonusesPerDay, signatures } = await generateSignature
      .mutateAsync({
        tokenIds,
        owner: account.address!,
      })
      .catch((e) => {
        // Post-supabase-native: any error from generateSignature surfaces
        // here as a plain Error. We can't discriminate "not enough boosts"
        // from other errors anymore without a richer error type; just show
        // the generic message.
        showSnackbar(
          t('BULK_ACTION_BOOST_NOT_ENOUGH_BOOSTS', {
            boosts: tokenIds.length,
          }),
          { severity: 'error' },
        );
        throw e;
      });

    try {
      const txHash = await boostMutation.mutateAsync({
        tokenIds,
        bonusesPerDay,
        signatures,
      });
      await clients.base.waitForTransactionReceipt({
        hash: txHash,
        timeout: READ_RECEIPT_TIMEOUT,
        confirmations: 4,
      });
      afterBoostTransaction.mutate({
        tokenIds,
        success: true,
      });
    } catch (e: unknown) {
      afterBoostTransaction.mutate({
        tokenIds,
        success: false,
      });
      throw e;
    }
  }, [personas]);

  const handleClose = () => {
    generateSignature.reset();
    boostMutation.reset();
    onClose();
  };

  return (
    <BaseStakingModal
      personas={personas}
      status={status}
      title={t('BOOST_MODAL_TITLE')}
      subtitle={t('BOOST_MODAL_SUBTITLE')}
      completedText={t('BOOST_MODAL_COMPLETED_TEXT')}
      onClose={handleClose}
      actions={[
        <Action
          label={t('BOOST_MODAL_ACTION_CONFIRM')}
          onClick={handleBoost}
          fullWidth
        />,
        <Action
          label={t('BOOST_MODAL_ACTION_CANCEL')}
          onClick={handleClose}
          variant="secondary"
          color="onSurface"
          background="surface"
        />,
      ]}
      {...props}
    >
      {status === 'error' && (
        <StakingAlert>{t('STAKING_ALERT_ERROR')}</StakingAlert>
      )}
      <StakingStep
        title={t('STAKING_STEP_BOOST_TITLE')}
        description={t('STAKING_STEP_BOOST_DESCRIPTION', {
          rewards: personas.length * BONUS_PER_DAY,
        })}
        status={status}
        type="boost"
      />
    </BaseStakingModal>
  );
};
